import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { BarChart3, Circle, PieChart } from "lucide-react";
import { ClipLoader } from "react-spinners";
import "./styles/categorycard.css";

const centerTextPlugin = {
  id: "dashboardCenterText",
  afterDraw(chart) {
    if (chart.config.type !== "doughnut" || !chart.chartArea) return;
    const values = chart.data.datasets[0]?.data || [];
    const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
    if (!total) return;

    const { ctx, chartArea } = chart;
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#101828";
    ctx.font = "600 17px Poppins, sans-serif";
    ctx.fillText(new Intl.NumberFormat("en-GB", { notation: "compact" }).format(total), x, y - 5);
    ctx.fillStyle = "#98a2b3";
    ctx.font = "500 10px Poppins, sans-serif";
    ctx.fillText("TOTAL", x, y + 14);
    ctx.restore();
  },
};

Chart.register(...registerables, ChartDataLabels, centerTextPlugin);

const PALETTE = ["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706"];

const formatApiDate = (date) => {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  return adjustedDate.toISOString().split("T")[0];
};

const normaliseEntries = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return [];
  return Object.entries(data)
    .map(([label, value]) => [label || "Unspecified", Number(value) || 0])
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
};

const buildChartData = (data, label) => {
  const entries = normaliseEntries(data);
  return {
    labels: entries.map(([entryLabel]) => entryLabel),
    datasets: [
      {
        label,
        data: entries.map(([, value]) => value),
        backgroundColor: PALETTE,
        borderColor: "#ffffff",
        borderWidth: 3,
        borderRadius: 5,
        hoverBorderWidth: 3,
        hoverOffset: 5,
      },
    ],
  };
};

const ChartLoadingCard = () => (
  <div className="dashboard-chart-loading" role="status">
    <div className="dashboard-chart-loading-line"></div>
    <div className="dashboard-chart-loading-ring"></div>
    <span className="visually-hidden">Loading chart</span>
  </div>
);

const EmptyChart = () => (
  <div className="dashboard-chart-empty">
    <PieChart size={24} aria-hidden="true" />
    <span>No data for this selection</span>
  </div>
);

const CategoryCard = ({
  selectedTable,
  displaySource,
  startDate,
  endDate,
  dashboardView = false,
  enabled = true,
}) => {
  const [categoryData, setCategoryData] = useState({});
  const [removalReasonData, setRemovalReasonData] = useState({});
  const [unitCostData, setUnitCostData] = useState({});
  const [chartType, setChartType] = useState("doughnut");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;

  useEffect(() => {
    if (!enabled) {
      setLoading(true);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    const params = {
      table: selectedTable,
      start_date: startDate ? formatApiDate(startDate) : null,
      end_date: endDate ? formatApiDate(endDate) : null,
    };

    setLoading(true);
    setError(null);

    const fetchChartData = async () => {
      try {
        const categoryResponse = await axios.get(`${baseUrl}/api/category_data/`, {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setCategoryData(categoryResponse.data.category_data || {});
        setRemovalReasonData(categoryResponse.data.removal_reason_data || {});

        const unitCostResponse = await axios.get(`${baseUrl}/api/unit-cost-category/`, {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        setUnitCostData(unitCostResponse.data[selectedTable] || {});
      } catch (requestError) {
        if (requestError.code !== "ERR_CANCELED") {
          console.error("Error fetching chart data:", requestError);
          setError("Charts could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchChartData();

    return () => controller.abort();
  }, [baseUrl, selectedTable, startDate, endDate, enabled]);

  const chartOptions = useMemo(() => {
    const isBar = chartType === "bar";
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 450 },
      cutout: chartType === "doughnut" ? "68%" : undefined,
      layout: { padding: isBar ? { top: 8 } : 6 },
      plugins: {
        legend: {
          display: !isBar,
          position: "bottom",
          labels: {
            boxWidth: 9,
            boxHeight: 9,
            padding: 14,
            usePointStyle: true,
            pointStyle: "circle",
            color: "#475467",
            font: { size: 11, family: "Poppins, sans-serif" },
            generateLabels(chart) {
              const labels = chart.data.labels || [];
              const values = chart.data.datasets[0]?.data || [];
              const colors = chart.data.datasets[0]?.backgroundColor || [];
              const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
              return labels.map((label, index) => ({
                text: `${String(label).length > 22 ? `${String(label).slice(0, 22)}…` : label} ${total ? Math.round((values[index] / total) * 100) : 0}%`,
                fillStyle: colors[index],
                strokeStyle: colors[index],
                hidden: !chart.getDataVisibility(index),
                index,
              }));
            },
          },
          onClick(event, legendItem, legend) {
            legend.chart.toggleDataVisibility(legendItem.index);
            legend.chart.update();
          },
        },
        datalabels: {
          display(context) {
            const values = context.dataset.data;
            const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
            const percentage = total ? (context.dataset.data[context.dataIndex] / total) * 100 : 0;
            return isBar || percentage >= 8;
          },
          formatter(value, context) {
            if (isBar) return new Intl.NumberFormat("en-GB", { notation: "compact" }).format(value);
            const total = context.dataset.data.reduce((sum, item) => sum + Number(item || 0), 0);
            return total ? `${Math.round((value / total) * 100)}%` : "";
          },
          color: isBar ? "#344054" : "#ffffff",
          anchor: isBar ? "end" : "center",
          align: isBar ? "top" : "center",
          offset: isBar ? 2 : 0,
          font: { size: 11, weight: "600", family: "Poppins, sans-serif" },
        },
        tooltip: {
          backgroundColor: "#101828",
          padding: 11,
          cornerRadius: 8,
          titleFont: { family: "Poppins, sans-serif", size: 12 },
          bodyFont: { family: "Poppins, sans-serif", size: 12 },
          callbacks: {
            label(context) {
              const values = context.dataset.data;
              const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
              const value = Number(context.raw || 0);
              const percentage = total ? ((value / total) * 100).toFixed(1) : "0.0";
              return ` ${value.toLocaleString()} · ${percentage}%`;
            },
          },
        },
      },
      scales: isBar
        ? {
            x: {
              grid: { display: false },
              ticks: { color: "#667085", font: { size: 10 }, maxRotation: 28, minRotation: 0 },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grace: "15%",
              grid: { color: "#eef1f5" },
              ticks: { color: "#98a2b3", precision: 0, font: { size: 10 } },
              border: { display: false },
            },
          }
        : undefined,
    };
  }, [chartType]);

  const charts = [
    { title: "Top categories", data: buildChartData(categoryData, "Categories") },
    { title: "Removal reasons", data: buildChartData(removalReasonData, "Removal reasons") },
    { title: "Unit cost", data: buildChartData(unitCostData, "Unit cost") },
  ];

  const ChartComponent =
    chartType === "bar" ? Bar : chartType === "pie" ? Pie : Doughnut;

  const legacyChartOptions = {
    plugins: {
      datalabels: {
        formatter(value, context) {
          const total = context.dataset.data.reduce(
            (sum, item) => sum + Number(item || 0),
            0
          );
          return total ? `${((value / total) * 100).toFixed(1)}%` : "";
        },
        color: "#fff",
        font: { weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label(context) {
            const total = context.dataset.data.reduce(
              (sum, item) => sum + Number(item || 0),
              0
            );
            const value = Number(context.raw || 0);
            const percentage = total ? ((value / total) * 100).toFixed(1) : "0.0";
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { display: false } },
    },
  };

  if (!dashboardView) {
    if (loading) {
      return (
        <div className="spinner-container">
          <ClipLoader size={50} color="#2563eb" loading />
        </div>
      );
    }

    if (error) return <div className="error-message">{error}</div>;

    return (
      <div className="analysis-charts">
        {charts.map((chart, index) => (
          <div className="chart-container" key={chart.title}>
            <div className="chart">
              <div className="chart-top-section">
                <h3>
                  {[
                    "Top 5 Categories",
                    "Top 5 Removal Reasons",
                    "Unit Cost Classification",
                  ][index]}{" "}
                  - {selectedTable}
                </h3>
              </div>
              {chart.data.datasets[0].data.length ? (
                <Doughnut data={chart.data} options={legacyChartOptions} />
              ) : (
                <EmptyChart />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className="dashboard-charts" aria-label="Inventory charts">
      <div className="chart-view-switcher" role="group" aria-label="Chart type">
        <button
          type="button"
          className={chartType === "doughnut" ? "active" : ""}
          onClick={() => setChartType("doughnut")}
        >
          <Circle size={15} aria-hidden="true" />
          Doughnut
        </button>
        <button
          type="button"
          className={chartType === "pie" ? "active" : ""}
          onClick={() => setChartType("pie")}
        >
          <PieChart size={15} aria-hidden="true" />
          Pie
        </button>
        <button
          type="button"
          className={chartType === "bar" ? "active" : ""}
          onClick={() => setChartType("bar")}
        >
          <BarChart3 size={15} aria-hidden="true" />
          Bar
        </button>
      </div>

      {error ? (
        <div className="dashboard-chart-error" role="alert">{error}</div>
      ) : (
        <div className="analysis-charts dashboard-analysis-charts">
          {loading
            ? [0, 1, 2].map((item) => <ChartLoadingCard key={item} />)
            : charts.map((chart) => (
                <article className="chart-container" key={chart.title}>
                  <div className="chart-top-section">
                    <h2>{chart.title}</h2>
                    <span>{displaySource || selectedTable}</span>
                  </div>
                  <div className="dashboard-chart-canvas">
                    {chart.data.datasets[0].data.length ? (
                      <ChartComponent
                        data={chart.data}
                        options={chartOptions}
                        aria-label={`${chart.title} for ${displaySource || selectedTable}`}
                      />
                    ) : (
                      <EmptyChart />
                    )}
                  </div>
                </article>
              ))}
        </div>
      )}
    </section>
  );
};

export default CategoryCard;
